import React, { useState } from 'react';
import { ReviewModel, Page } from '../../typings/reviews';
import {
  TextCustomProps,
  TextAppearance,
  TextColor,
  TextSize,
  TextLineHeight,
  TextAlignment,
  dropdownOptionType,
  DropdownCustom,
  DropdownOption,
} from '@vw-marketing/us-components';
import _ from 'lodash';
import { getPages } from '../../utils/general';
import {
  ReviewsPagesContainer,
  ReviewsBarContainer,
  DropdownSectionContainer,
  StyledRatingDropdownWrapper,
  StyledSortDropdownWrapper,
  StyledHiddenPage,
} from './styled';
import PageInformation from '../page-information';
import ReviewsHandler from './reviews-handle';
import { onHandleFilterSelectTrackingEvent } from '../../utils/tracking-helpers';
import { FeatureAppTrackingManager } from '../../services/use-tracking-manager';
import { AppConstants } from '../../utils/app-constants';

interface ReviewsPagesProps {
  readonly reviews?: ReviewModel;
  readonly scrollHandler: () => any;
  readonly trackingManager?: FeatureAppTrackingManager;
}

export const ratingOptions: dropdownOptionType[] = [
  { value: 'all', label: 'All Stars' },
  { value: '1', label: '1 Star' },
  { value: '2', label: '2 Stars' },
  { value: '3', label: '3 Stars' },
  { value: '4', label: '4 Stars' },
  { value: '5', label: '5 Stars' },
];

export const sortOptions: dropdownOptionType[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
];

const ReviewsPages: React.FC<ReviewsPagesProps> = props => {
  const { reviews, scrollHandler, trackingManager } = props;
  const initialPages = getPages(
    reviews?.page.totalElements || 0,
    AppConstants.ALL_REVIEWS_SIZE as number,
  );
  const [rating, setRating] = useState<dropdownOptionType | undefined>(
    undefined,
  );
  const [sort, setSort] = useState<dropdownOptionType | undefined>(undefined);
  const [page, setPage] = useState<Page | undefined>(undefined);
  const [isHiddenPage, setIsHiddenPage] = useState(false);
  const [resetFilters, setResetFilters] = useState(false);
  const [pageNumber, setPageNumber] = useState<number>(
    AppConstants.API_PAGINATION_BASE as number,
  );

  // Component constants
  const ratingLabel = 'Filter by Rating';
  const sortLabel = 'Sort by All Reviews';
  const selectedRatingLabel = 'Filter by ';
  const selectedSortLabel = 'Sort by the ';

  const dropdownTextStyle: TextCustomProps = {
    appearance: TextAppearance.label150,
    style: {
      color: TextColor.inherit,
      fontSize: TextSize.TextSize16,
      lineHeight: TextLineHeight.TextLineHeight20,
      textAlign: TextAlignment.left,
    },
  };

  /**
   * Handle page bar change
   * @param number
   */
  const handlePageChange = (number: number) => {
    if (isHiddenPage) return;
    scrollHandler();
    setPageNumber(number);
    page && setPage({ ...page, number: number });
  };

  /**
   * Handle new request for rating selected
   * @param rating
   */
  const handleRatingChange = (value: dropdownOptionType) => {
    if (isHiddenPage) return;
    setRating(value);
    setSort(undefined);
    setResetFilters(false);

    // Tracking event
    value.value !== rating?.value &&
      onHandleFilterSelectTrackingEvent(
        'rating',
        value.value,
        [value.value || ''],
        trackingManager,
        reviews,
      );
  };

  /**
   * handle sort filter
   * @param value
   */
  const handleSortChange = (value: dropdownOptionType) => {
    if (isHiddenPage) return;
    setSort(value);

    // Tracking event
    value.value !== sort?.value &&
      onHandleFilterSelectTrackingEvent(
        'sort',
        value.value,
        [value?.value || '', rating?.value || ''],
        trackingManager,
        reviews,
      );
  };

  /**
   * Reset all applied filters
   */
  const handleResetFilters = () => {
    setRating(ratingOptions[0]);
    setSort(undefined);
    setPageNumber(AppConstants.API_PAGINATION_BASE as number);
    setResetFilters(true);
    setPage(undefined);
  };

  React.useEffect(() => {
    if (isHiddenPage) {
      setPage(undefined);
    }
    return () => {};
  }, [isHiddenPage]);

  React.useEffect(() => {
    return () => {
      handleResetFilters();
    };
  }, []);

  return (
    <ReviewsPagesContainer>
      <ReviewsBarContainer>
        {!isHiddenPage && page ? (
          <PageInformation reviewsPage={page} />
        ) : (
          <StyledHiddenPage />
        )}
        <DropdownSectionContainer>
          <StyledRatingDropdownWrapper tabIndex={0}>
            <DropdownCustom
              value={!resetFilters ? rating : undefined}
              selectedLabel={selectedRatingLabel}
              placeholder={ratingLabel}
              onChange={handleRatingChange}
              inputTextAppearance={dropdownTextStyle}
              disabled={isHiddenPage}
            >
              {ratingOptions.map((option: dropdownOptionType) => (
                <DropdownOption
                  value={option.value}
                  label={option.label}
                  key={option.value}
                />
              ))}
            </DropdownCustom>
          </StyledRatingDropdownWrapper>
          <StyledSortDropdownWrapper tabIndex={0}>
            <DropdownCustom
              value={sort}
              placeholder={sortLabel}
              selectedLabel={selectedSortLabel}
              onChange={handleSortChange}
              inputTextAppearance={dropdownTextStyle}
              disabled={isHiddenPage}
            >
              {sortOptions.map((option: dropdownOptionType) => (
                <DropdownOption
                  value={option.value}
                  label={option.label}
                  key={option.value}
                />
              ))}
            </DropdownCustom>
          </StyledSortDropdownWrapper>
        </DropdownSectionContainer>
      </ReviewsBarContainer>
      <ReviewsHandler
        reviewsModel={reviews}
        pageNumber={pageNumber}
        pageData={page}
        setPageData={setPage}
        modelName={reviews?.modelConfiguration?.requestName || ''}
        reverse={sort ? sort.value === 'oldest' : undefined}
        rating={rating?.value}
        handlePageChange={handlePageChange}
        handleResetFilters={handleResetFilters}
        trackingManager={trackingManager}
        hidePageData={setIsHiddenPage}
      />
    </ReviewsPagesContainer>
  );
};

export default ReviewsPages;
